import React from 'react';
import { FiPaperclip, FiX } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { getSimpleProjectList } from '../../api/projects';
import FormField from '../../components/ui/FormField';
import { Input, Select, Textarea, NumberInput } from '../../components/ui/Input';
import FileUploader from '../../components/ui/FileUploader';

export const CATEGORY_OPTIONS = [
  'TRAVEL',
  'MEALS',
  'LODGING',
  'OFFICE_SUPPLIES',
  'COMMUNICATION',
  'TRAINING',
  'MEDICAL',
  'CLIENT_ENTERTAINMENT',
  'OTHER',
];

export const DEPARTMENT_OPTIONS = ['HR', 'FINANCE'];

export default function ReimbursementFormInner({ form, update, errors, file, setFile, isEdit = false }) {
  const { data: projects = [] } = useQuery({
    queryKey: ['projects', 'simple'],
    queryFn: getSimpleProjectList,
  });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField
        label="Title"
        required
        error={errors.title}
        className="md:col-span-2"
      >
        <Input
          value={form.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Brief subject, e.g. Client meeting cab fare"
          error={!!errors.title}
          autoFocus
        />
      </FormField>

      <FormField
        label="Description"
        className="md:col-span-2"
        hint="Optional context for the approver."
      >
        <Textarea
          rows={3}
          value={form.description || ''}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="What is being reimbursed and why?"
        />
      </FormField>

      <FormField label="Amount (INR)" required error={errors.amount}>
        <NumberInput
          min="0"
          step="0.01"
          value={form.amount}
          onChange={(e) => update({ amount: e.target.value })}
          error={!!errors.amount}
          placeholder="0.00"
        />
      </FormField>

      <FormField label="Category" required error={errors.category}>
        <Select
          value={form.category}
          onChange={(e) => update({ category: e.target.value })}
          error={!!errors.category}
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c.replace(/_/g, ' ')}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Payment Mode">
        <Select
          value={form.paymentMode || ''}
          onChange={(e) => update({ paymentMode: e.target.value })}
        >
          <option value="BANK_TRANSFER">Bank Transfer</option>
          <option value="CASH">Cash</option>
          <option value="CHEQUE">Cheque</option>
        </Select>
      </FormField>

      <FormField label="Transaction Date" hint="Optional — Date of the expense.">
        <Input
          type="date"
          value={form.transactionDate || ''}
          onChange={(e) => update({ transactionDate: e.target.value })}
        />
      </FormField>

      <FormField
        label="Approval Department"
        hint="Optional — picks the routing chain. Leave blank to use the default."
        className="md:col-span-2"
      >
        <Select
          value={form.department || ''}
          onChange={(e) => update({ department: e.target.value })}
        >
          <option value="">Default</option>
          {DEPARTMENT_OPTIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Project" required error={errors.projectId}>
        <Select
          value={form.projectId || ''}
          onChange={(e) => update({ projectId: e.target.value ? Number(e.target.value) : '' })}
          error={!!errors.projectId}
        >
          <option value="">Select project…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.projectName}
            </option>
          ))}
        </Select>
      </FormField>

      {!isEdit && (
        <div className="md:col-span-2">
          <FormField
            label="Receipt / Attachment"
            hint="Optional — receipt or supporting document (max 10MB)."
          >
            {file ? (
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  <FiPaperclip className="text-ink-muted" />
                  <span className="truncate text-ink">{file.name}</span>
                  <span className="shrink-0 text-xs text-ink-muted">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="rounded p-1 text-ink-muted hover:bg-slate-100 hover:text-danger"
                  aria-label="Remove attachment"
                >
                  <FiX size={16} />
                </button>
              </div>
            ) : (
              <FileUploader variant="dropzone" onSelect={(f) => setFile(f)} />
            )}
          </FormField>
        </div>
      )}
    </div>
  );
}
